import React, { useEffect, useState } from 'react';
import './FundManagerPage.css';
import { ChevronLeft, ChevronRight, Banana, CandyCane, Computer, Flashlight  } from 'lucide-react';
import {Card, CardContent, StatCard} from "../../components/card-components";
import DashboardBase  from "../DashboardBase";
import {formatCurrency, formatDateIntl} from "../../util/currency_lookup";
import { Loader } from '../../components/loader';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import { useNavigate } from "react-router";

const FundsManagerPage = () => {
  const [stats, setStats] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useStateWithCallbackLazy({
    transactions: true,
    stats: true,
  });
  const [error, setError] = useState(null);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const isLoading = Object.values(loading).some(status => status);

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedSearch(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect((isLoading) => {
    if (!isLoading) {
        fetchTransactions(currentPage, debouncedSearch);
        fetchStats();
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search changes
    fetchTransactions(1, debouncedSearch);
  }, [debouncedSearch]);


  const fetchStats= () => {
    const token = localStorage.getItem('token');

    fetch(`${process.env.REACT_APP_API_URL}/api/v1/fund-manager/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setStats(data);
        setLoading(prev => ({ ...prev, stats: false }));
      })
      .catch((error) => {
        setError(error);
        setLoading(prev => ({ ...prev, stats: false }));
      });
  };

  const fetchTransactions = (page, search = '') => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError(new Error('No authentication token found'));
      return;
    }
    const indexOfLastTransaction = ((page - 1) * itemsPerPage);

    fetch(`${process.env.REACT_APP_API_URL}/api/v1/fund-manager?offset=${indexOfLastTransaction}&limit=${itemsPerPage}&search=${search}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setTransactions(data.rows);
        setTotal(data.total);
        setLoading(prev => ({ ...prev, transactions: false }));
      })
      .catch((error) => {
        setError(error);
        setLoading(prev => ({ ...prev, transactions: false }));
      });
  };

  if (isLoading) {
    <Loader loading={loading} />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const goToItem = (id) => {
    navigate(`/fund-manager-details/${id}`);
  };

  const goToCreate = () => {
    navigate(`/create-fund-manager`);
  };

  const paginate = async (pageNumber) => {
    setLoading(true, () => {
      setCurrentPage(pageNumber);
   });
  };

  return (
    <DashboardBase
      page="fund-manager"
      content= {
            <div className="dashboard-home">
                {/* Secondary Navigation */}

                <div className="dashboard-header">
                        <h1>Summary</h1>
                        <button
                                onClick={() => goToCreate()}
                                type="button" 
                                className="button secondary">
                        Create Manager</button>
                </div>
                {/* Stats Grid */}
                <div className="stats-grid">
                <div className="stats-container">
                    <StatCard 
                      title="Total Manager" 
                      value={stats.total_fund_managers} 
                      icon={() => <div className="text-xs"> <Banana/>  </div>}
                    />
                    <StatCard 
                      title="Active" 
                      value={stats.active_fund_managers} 
                      icon={() => <div className="text-xs"><CandyCane/> </div>}
                    />
                    <StatCard 
                      title="Passive" 
                      value={stats.passive_fund_managers} 
                      icon={() => <div className="text-xs"><Computer/> </div>}
                    />
                    <StatCard 
                      title="Avg Fund Per Manager" 
                      value={Math.round(stats.average_funds_managed * 100) / 100 } 
                      icon={() => <div className="text-xs"><Flashlight/> </div>}
                    />
                </div>
                </div>

                {/* Transactions Table */}
                <div className="transactions-section">
                <div className="stats-header">
                    <h2 className="stats-title">Fund Manager</h2>
                    <select className="select-control">
                    <option>All</option>
                    </select>
                </div>
                <Card>
                    <div className="tabs-header">
                    <div className="tabs">
                    <div className="search-section">
                        <div className="search-container">
                        <div className="search-wrapper">
                              <input
                                  type="text"
                                  placeholder="Search fund managers..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  className="search-input"
                              />
                              {isLoading && <span className="search-loading">Searching...</span>}
                          </div>
                        </div>
                    </div>
                    </div>
                    </div>
                    <div className="table-container">
                    <table>
                        <thead>
                        <tr>
                            {/* <th>Transaction date</th> */}
                            <th>Name</th>
                            <th>Address</th>
                            <th>Website</th>
                            <th>Creation</th>
                        </tr>
                        </thead>
                        <tbody>
                        {transactions.map((transaction, index) => (
                        <tr key={index} onClick={() => goToItem(transaction.fund_manager_id)}>
                            {/* <td>{transaction.fund_manager_id}</td> */}
                            <td> 
                              <span className="highlight-underline">
                                  {transaction.name}
                              </span>
                            </td>
                            
                            <td>{transaction.address}</td>
                            <td>{transaction.website}</td>
                            <td>{formatDateIntl(transaction.created_at)}</td>
                            {/* <td>{transaction.receiveAmount}</td>
                            <td><span className={`status ${transaction.status.toLowerCase()}`}>{transaction.status}</span></td> */}
                        </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                    {/* Pagination */}
                    <div className="pagination">
                    <div>
                        <p className="pagination-text">
                        Showing page {currentPage} of {Math.ceil(total / itemsPerPage)}
                        </p>
                    </div>
                    <div className="pagination-buttons">
                        <button
                        onClick={() => paginate(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-button"
                        >
                        <ChevronLeft className="icon" />
                        </button>
                        <button
                        onClick={() => paginate(p => p + 1)}
                        disabled={currentPage * itemsPerPage >= total}
                        className="pagination-button"
                        >
                        <ChevronRight className="icon" />
                        </button>
                    </div>
                    </div>
                </Card>
            </div>
        </div>
      }
    />
  );
}

export default FundsManagerPage;
